import React, {useEffect, useRef, useState} from 'react';
import {Calendar, CalendarChangeParams, CalendarProps} from 'primereact/calendar';
import useDebounce from "hooks/useDebounce";
import {ChronoUnit} from "utils/time-utils";

type CalendarWrapperProps = {
    value: Date | undefined
    onChange(date: Date): void
    dateFrequency?: ChronoUnit
    debounceTimeInMs?: number
} & Omit<CalendarProps, 'value' | 'onChange'>

const CalendarWrapper = ({value, onChange, dateFrequency, debounceTimeInMs, ...props}: CalendarWrapperProps) => {
    const valueRef = useRef<Date | null>(null);
    const [date, setDate] = useState<Date | null>(null);
    const debouncedValue = useDebounce(date, {delay: debounceTimeInMs});

    useEffect(() => {
        const handleEnterPressed = (event: KeyboardEvent) => (event.code === 'Enter' || event.code === 'NumpadEnter') && replaceDateValue()

        window.addEventListener('keydown', handleEnterPressed);

        return () => window.removeEventListener('keydown', handleEnterPressed);
    }, [])

    useEffect(() => {
        debouncedValue && onChange(debouncedValue)
    }, [debouncedValue])

    useEffect(() => {
        valueRef.current && (valueRef.current = null)
    }, [value])

    const replaceDateValue = () => {
        if (valueRef.current != null && value !== valueRef.current) {
            onChange(valueRef.current)
        }
    }

    const onCalendarChange = (e: CalendarChangeParams) => {
        if (!e.value) return

        const pickedDate = e.value as Date

        if (dateFrequency === 'DAYS') {
            pickedDate.setHours(0, 0, 0, 0);
        } else if (dateFrequency === 'HOURS') {
            pickedDate.setMinutes(0, 0, 0);
        }

        if (e.originalEvent.type === "input") {
            valueRef.current = pickedDate
        } else if (e.originalEvent.type === "click") {
            onChange(pickedDate)
        } else if (e.originalEvent.type === "mousedown") {
            setDate(pickedDate)
        }
    }

    return <Calendar value={value}
                     onChange={onCalendarChange}
                     showIcon={true}
                     iconPos={"left"}
                     dateFormat={"yy-mm-dd"}
                     showTime={true}
                     onBlur={() => replaceDateValue()}
                     {...props}
    />
}

export default CalendarWrapper;